import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  CircleDollarSign,
  CloudUpload,
  MonitorCheck,
  MonitorX,
  PlayCircle,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { api } from './api'
import type { Page } from './types'
import { PageHeader, StatusPill, TableShell } from './ui'

interface Device {
  id: string
  name: string
  status: string
  appVersion: string
  lastSeenAt: string | null
}

interface Billing {
  status: string
  deviceLimit: number
  storageLimitBytes: number
  usage: { devices: number; users: number; storageBytes: number }
}

interface Analytics {
  plays: number
  completedPlays: number
  onlineDevices: number
  offlineDevices: number
}

interface Publication {
  id: string
  version: number
  name: string
  status: string
  manifestCount: number
  createdAt: string
}

function Metric({
  label,
  value,
  detail,
  icon: Icon,
  tone = 'neutral',
}: {
  label: string
  value: string | number
  detail: string
  icon: typeof MonitorCheck
  tone?: 'good' | 'danger' | 'neutral'
}) {
  return (
    <Card>
      <CardContent className='flex items-start justify-between p-5'>
        <div>
          <p className='text-xs font-bold tracking-[0.14em] text-muted-foreground uppercase'>
            {label}
          </p>
          <p className='mt-3 text-3xl font-black tracking-tight'>{value}</p>
          <p className='mt-2 text-xs text-muted-foreground'>{detail}</p>
        </div>
        <span
          className={`grid size-10 place-items-center rounded-xl ${
            tone === 'good'
              ? 'bg-emerald-500/10 text-emerald-600'
              : tone === 'danger'
                ? 'bg-red-500/10 text-red-600'
                : 'bg-muted text-muted-foreground'
          }`}
        >
          <Icon className='size-5' />
        </span>
      </CardContent>
    </Card>
  )
}

export function DashboardPage() {
  const analyticsRange = useMemo(() => {
    const to = new Date()
    const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000)
    return {
      from: from.toISOString(),
      to: to.toISOString(),
    }
  }, [])
  const billing = useQuery({
    queryKey: ['billing-summary'],
    queryFn: () => api<Billing>('/billing/summary'),
  })
  const devices = useQuery({
    queryKey: ['devices', 'dashboard'],
    queryFn: () => api<Page<Device>>('/devices?limit=5'),
  })
  const analytics = useQuery({
    queryKey: ['analytics', analyticsRange.from, analyticsRange.to],
    queryFn: () =>
      api<Analytics>(
        `/analytics/overview?from=${encodeURIComponent(analyticsRange.from)}&to=${encodeURIComponent(analyticsRange.to)}`
      ),
  })
  const publications = useQuery({
    queryKey: ['publications'],
    queryFn: () => api<Publication[]>('/publications'),
  })

  const usagePercent = billing.data
    ? Math.min(
        100,
        Math.round(
          (billing.data.usage.storageBytes / billing.data.storageLimitBytes) *
            100
        )
      )
    : 0
  const latestPublication = publications.data?.[0]

  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Fleet control'
        title='Good morning. Your screens at a glance.'
        description='Live health, recent playback, entitlement, and publishing status from the same authoritative API your players use.'
        action={
          <Button asChild>
            <Link to='/app/publish'>
              <CloudUpload className='size-4' />
              Publish content
            </Link>
          </Button>
        }
      />

      <div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
        <Metric
          label='Screens online'
          value={analytics.data?.onlineDevices ?? '—'}
          detail={`${billing.data?.usage.devices ?? 0} of ${billing.data?.deviceLimit ?? '—'} enrolled`}
          icon={MonitorCheck}
          tone='good'
        />
        <Metric
          label='Screens offline'
          value={analytics.data?.offlineDevices ?? '—'}
          detail='Heartbeat threshold: two minutes'
          icon={MonitorX}
          tone={
            (analytics.data?.offlineDevices ?? 0) > 0 ? 'danger' : 'neutral'
          }
        />
        <Metric
          label='7-day plays'
          value={(analytics.data?.plays ?? 0).toLocaleString()}
          detail={`${(analytics.data?.completedPlays ?? 0).toLocaleString()} completed`}
          icon={PlayCircle}
        />
        <Metric
          label='Storage used'
          value={`${usagePercent}%`}
          detail={
            billing.data
              ? `${(billing.data.usage.storageBytes / 1024 ** 3).toFixed(1)} of ${(billing.data.storageLimitBytes / 1024 ** 3).toFixed(0)} GB`
              : 'Loading quota'
          }
          icon={CircleDollarSign}
        />
      </div>

      <div className='grid gap-5 xl:grid-cols-[1.4fr_.6fr]'>
        <Card>
          <CardHeader className='flex-row items-center justify-between'>
            <div>
              <CardTitle className='text-base'>Screen health</CardTitle>
              <p className='mt-1 text-xs text-muted-foreground'>
                Most recently enrolled screens
              </p>
            </div>
            <Button asChild size='sm' variant='outline'>
              <Link to='/app/devices'>View fleet</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {devices.data?.items.length ? (
              <TableShell headers={['Screen', 'State', 'Player', 'Last seen']}>
                {devices.data.items.map((device) => (
                  <tr key={device.id}>
                    <td className='px-4 py-3 font-medium'>{device.name}</td>
                    <td className='px-4 py-3'>
                      <StatusPill value={device.status} />
                    </td>
                    <td className='px-4 py-3 text-muted-foreground'>
                      {device.appVersion}
                    </td>
                    <td className='px-4 py-3 text-muted-foreground'>
                      {device.lastSeenAt
                        ? new Date(device.lastSeenAt).toLocaleString()
                        : 'Never'}
                    </td>
                  </tr>
                ))}
              </TableShell>
            ) : (
              <div className='grid min-h-40 place-items-center text-center text-sm text-muted-foreground'>
                <div>
                  <p>No screens enrolled yet.</p>
                  <Button asChild className='mt-4' size='sm'>
                    <Link to='/app/devices'>Pair a screen</Link>
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className='overflow-hidden'>
          <CardHeader>
            <CardTitle className='text-base'>Current publication</CardTitle>
          </CardHeader>
          <CardContent>
            {latestPublication ? (
              <div>
                <div className='rounded-xl bg-zinc-950 p-5 text-white'>
                  <div className='flex items-center justify-between'>
                    <p className='text-xs font-bold tracking-widest text-zinc-500 uppercase'>
                      Version {latestPublication.version}
                    </p>
                    <StatusPill value={latestPublication.status} />
                  </div>
                  <p className='mt-7 text-xl font-bold'>
                    {latestPublication.name}
                  </p>
                  <p className='mt-2 text-sm text-zinc-400'>
                    {latestPublication.manifestCount} deterministic screen
                    manifests
                  </p>
                </div>
                <p className='mt-4 text-xs leading-5 text-muted-foreground'>
                  Published{' '}
                  {new Date(latestPublication.createdAt).toLocaleString()}.
                  Players retain this and the previous version for atomic
                  rollback.
                </p>
              </div>
            ) : (
              <div className='grid min-h-44 place-items-center text-sm text-muted-foreground'>
                Nothing published yet.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
